/**
 * 63.14 step 3 (ARCHITECTURE §5.22 "modules as shapes, walls, doors"; DECISIONS 201 (3) walkable ground floors, (9) a
 * gate's real leaf): a modular building's hit shape from its layout — every module's parts (constants/modules.ts
 * MODULE_SHAPES) posed in the building's frame (core/modules.ts posesOf), as one HitboxDef in people the sim reads in
 * place of the builder's one box (core/buildingScale.ts structureHitbox): the bullets and sight meet the walls, the slabs
 * and the posts and pass the doors, the windows' panes and the wire (`pass`); the walk meets each wall as a closed row of
 * discs (63.9 / 67.62's rule in core/hits.ts outlineDiscs) and passes a door's opening between its jambs; the ground
 * floor's tiles are stepped over (walkable), the roof is over every head. A gate's leaves stand while the module is shut
 * (StructureState.shut: the explicit list; absent = SHUT_BY_DEFAULT's kinds shut, doors open) — solid sheet steel (kit 2),
 * so a shut gate stops the eye, the bullet and the foot; open, its leaves are out of the way and the opening passes all three. Pure; memoised per layout and shut set (the same HitboxDef object while nothing changes, so
 * the shapes' per-entity memo keeps a building's prims until a leaf moves). Behind GameConfig.modularBuildings.
 */
import type { HitboxDef, HitPart } from '../types/body';
import type { BuildingLayout, ModuleKind } from '../types/modules';
import type { StructureState } from '../types';
import { MODULE_SHAPES, SHUT_BY_DEFAULT, RUBBLE, RUBBLE_KINDS } from '../constants/modules';
import { posesOf } from './modules';

/** cos and sin of a quarter turn (exact). */
const COS = [1, 0, -1, 0] as const, SIN = [0, 1, 0, -1] as const;

/** The modules of `layout` whose leaves are shut for `s`: its own list, or the default (gates shut, doors open). */
export function shutOf(s: Partial<Pick<StructureState, 'shut'>>, layout: BuildingLayout): ReadonlySet<number> {
  if (s.shut) return new Set(s.shut);
  const out = new Set<number>();
  layout.modules.forEach((m, k) => { if (SHUT_BY_DEFAULT.has(m.kind)) out.add(k); });
  return out;
}

const ruins = new WeakMap<BuildingLayout, Required<Pick<StructureState, 'modules' | 'rubble'>>>();
/**
 * 72.1: a modular building's ruin (the structure at 0) — every module destroyed, each ground-floor wall-like one's rubble lying
 * (an upper storey's walls are down with its slab: a heap posed at their floor would hang in the air), for good
 * (the ruin's heaps are its own, not the decaying `StructureState.rubble`). The sim's shape (core/buildingScale.ts
 * structureHitbox) and the drawing (rendering/moduleLook.ts) read the same one, as a module destroyed alone is read; it was
 * the builder's old broken model drawn over the layout's shapes kept standing at that model's ruin height. Memoised per
 * layout (one `modules` array, so the hit shape's memo holds).
 */
export function layoutRuin(layout: BuildingLayout): Required<Pick<StructureState, 'modules' | 'rubble'>> {   // (shared: read, never written)
  let r = ruins.get(layout);
  if (!r) {
    r = { modules: layout.modules.map(() => 0), rubble: [] };
    layout.modules.forEach((m, k) => { if (RUBBLE_KINDS.has(m.kind) && (m.floor ?? 0) === 0) r!.rubble.push({ k, at: 0 }); });
    ruins.set(layout, r);
  }
  return r;
}

/** Whether a kind carries a leaf (its parts include one). */
export function hasLeaf(kind: ModuleKind): boolean {
  return MODULE_SHAPES[kind].some(p => p.leaf);
}

const memo = new WeakMap<BuildingLayout, Map<string, HitboxDef>>();
/** 63.14 step 4: per `modules` array (a new one each blow), the hitboxes by shut set and rubble. */
const wornMemo = new WeakMap<readonly number[], Map<string, HitboxDef>>();
type Rubble = ReadonlyArray<{ k: number; at: number; y?: number }>;

/**
 * The layout's hit shape with the given modules shut: a part per module part, turned by the module's quarter turns about
 * the vertical (x' = x cos + z sin, z' = −x sin + z cos; the half extents swap on an odd turn) and moved to the module's
 * pose (its floor's height in). Leaves only for the shut modules; `pass` parts kept (rays through).
 */
export function layoutHitbox(layout: BuildingLayout, shut: ReadonlySet<number>, modules?: readonly number[], rubble?: Rubble): HitboxDef {
  const key = [...shut].sort((a, b) => a - b).join(',') + (rubble?.length ? '|' + rubble.map(r => r.k).join(',') : '');
  let byShut = modules ? wornMemo.get(modules) : memo.get(layout);
  if (!byShut) { byShut = new Map(); if (modules) wornMemo.set(modules, byShut); else memo.set(layout, byShut); }
  const hit = byShut.get(key);
  if (hit) return hit;
  const heaps = new Map(rubble?.map(r => [r.k, r]) ?? []);
  const parts: HitPart[] = [];
  const poses = posesOf(layout);
  for (const pose of poses) {
    const m = layout.modules[pose.index], turn = m.turn ?? 0, c = COS[turn], s = SIN[turn];
    const isShut = shut.has(pose.index);
    if (modules && modules[pose.index] <= 0) {   // 63.14 step 4: destroyed — its shape gone (a breach); its rubble a low heap across its run, if any
      // (72.3 step 3: a fallen module's heap where it landed, `y`; a slab's a deck across its cell)
      const heap = heaps.get(pose.index), cell = m.at === 'cell';
      if (heap) parts.push({ shape: 'box', centre: [pose.x, (heap.y ?? pose.y) + RUBBLE.heightPeople / 2, pose.z], half: [cell ? 1 : turn % 2 ? RUBBLE.halfAcrossPeople : 1, RUBBLE.heightPeople / 2, cell ? 1 : turn % 2 ? 1 : RUBBLE.halfAcrossPeople] });
      continue;
    }
    for (const p of MODULE_SHAPES[m.kind]) {
      if (p.leaf && !isShut) continue;
      if (p.open && isShut) continue;   // 63.16 (6): a door's open leaf, there only while it is open
      const [x, y, z] = p.centre, [hx, hy, hz] = p.half;
      const part: HitPart = { shape: 'box', centre: [pose.x + x * c + z * s, pose.y + y, pose.z - x * s + z * c], half: [turn % 2 ? hz : hx, hy, turn % 2 ? hx : hz] };
      if (p.pass) part.pass = true;
      parts.push(part);
    }
  }
  const def: HitboxDef = { ruler: 'person', parts, modular: true };
  byShut.set(key, def);
  return def;
}
